import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { useDeletePromotionCodes, usePromotionCodeById } from '@/lib/api/promotionCodes';

interface DeletePromotionCodeModalProps {
  codeId?: string;
  onClose: () => void;
  onSuccess?: () => void;
}

export const DeletePromotionCodeModal = ({
  codeId,
  onClose,
  onSuccess,
}: DeletePromotionCodeModalProps) => {
  if (!codeId) return null;
  return <DeletePromotionCodeModalContent codeId={codeId} onClose={onClose} onSuccess={onSuccess} />;
};

const DeletePromotionCodeModalContent = ({
  codeId,
  onClose,
  onSuccess,
}: {
  codeId: string;
  onClose: () => void;
  onSuccess?: () => void;
}) => {
  const { t } = useTranslation(['stores', 'common']);
  const { toast } = useToast();
  const { data: promotionCode, isLoading } = usePromotionCodeById(codeId);
  const deleteMutation = useDeletePromotionCodes();

  const handleDelete = async () => {
    try {
      await deleteMutation.mutateAsync({ ids: [codeId] });
      toast({
        title: t('common:success'),
        description: t('stores:promotionCodes.deleteSuccess'),
      });
      onSuccess?.();
      onClose();
    } catch {
      toast({
        title: t('common:error'),
        description: t('stores:promotionCodes.deleteError'),
        variant: 'destructive',
      });
    }
  };

  return (
    <AlertDialog open onOpenChange={onClose}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {t('stores:promotionCodes.modals.delete.title')}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {isLoading
              ? t('stores:promotionCodes.modals.delete.loading')
              : t('stores:promotionCodes.modals.delete.description', { code: promotionCode?.code ?? '' })}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <Button variant="outline" onClick={onClose} disabled={deleteMutation.isPending}>
            {t('common:cancel')}
          </Button>
          <Button
            variant="destructive"
            onClick={() => void handleDelete()}
            disabled={deleteMutation.isPending || isLoading}
          >
            {deleteMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            {t('common:delete')}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
